import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/context/LanguageContext";
import JsonLd from "@/components/JsonLd";

// FAQ générique. Par défaut : version claire (blogue). Avec `dark` + `image` : version
// sombre de l'accueil, boutons arrondis à gauche, grande image à droite.
export default function FAQ({ items, jsonLdId = "faq-home", eyebrow, title, testId = "faq-section", dark = false, image = null }) {
  const { t } = useLanguage();
  const faqs = items || t("faq.items");

  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: (faqs || []).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const accordion = (
    <Accordion type="single" collapsible className="w-full space-y-3">
      {faqs.map((f, i) => (
        <AccordionItem
          key={f.q}
          value={`item-${i}`}
          data-testid={`faq-item-${i}`}
          className={dark ? "border-white/10 rounded-2xl bg-white/5 overflow-hidden" : "border-slate-200"}
        >
          <AccordionTrigger
            data-testid={`faq-trigger-${i}`}
            className={
              dark
                ? "text-left font-display font-semibold text-white hover:text-[#C5A880] hover:no-underline px-5 py-4"
                : "text-left font-heading text-lg font-medium text-slate-900 hover:text-blue-600"
            }
          >
            {f.q}
          </AccordionTrigger>
          <AccordionContent
            data-testid={`faq-content-${i}`}
            className={`leading-relaxed text-base ${dark ? "text-slate-300 px-5" : "text-slate-600"}`}
          >
            {f.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

  const header = (
    <div className={image ? "" : "text-center mb-12"}>
      <div className={`text-xs tracking-[0.3em] uppercase font-display font-semibold ${dark ? "text-[#C5A880]" : "text-blue-600"}`}>
        {eyebrow || t("faq.eyebrow")}
      </div>
      <h2
        className={
          dark
            ? "font-display text-3xl lg:text-5xl font-bold tracking-tight leading-[1.1] mt-3 mb-10"
            : "font-heading text-3xl lg:text-5xl font-normal tracking-tight leading-[1.25] text-slate-900 mt-3"
        }
      >
        {title || t("faq.title")}
      </h2>
    </div>
  );

  return (
    <section
      data-testid={testId}
      className={`py-16 sm:py-24 lg:py-32 ${dark ? "bg-slate-950 text-white border-t border-white/5" : "bg-white"}`}
    >
      <JsonLd id={jsonLdId} data={faqLd} />
      <div className={`mx-auto px-6 lg:px-8 ${image ? "max-w-7xl" : "max-w-3xl"}`}>
        {image ? (
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div>
              {header}
              {accordion}
            </div>
            <div className="rounded-3xl overflow-hidden aspect-[4/5] bg-slate-900 border border-white/10 lg:sticky lg:top-28">
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        ) : (
          <>
            {header}
            {accordion}
          </>
        )}

        <p className={`mt-10 ${image ? "" : "text-center"} ${dark ? "text-slate-300" : "text-slate-600"}`}>
          {t("faq.more")}{" "}
          <Link
            to="/contact"
            data-testid="faq-contact-link"
            className={`inline-flex items-center gap-1 font-medium ${dark ? "text-[#C5A880] hover:text-[#E2D3BE]" : "text-blue-600 hover:text-blue-700"}`}
          >
            {t("faq.moreLink")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </p>
      </div>
    </section>
  );
}
